/**
 * budget-rollover-derive.ts — True envelope accumulation for rollover budgets (D7 B5).
 *
 * Replaces the 1-month-back shortcut in bento-derive with a full history walk.
 * Pure, no side effects. All numbers must match what bento-derive computes for
 * the current month's `spent` so budget tab numbers stay consistent.
 *
 * IMPORTANT: month keys are YYYY-MM strings. Date helpers from src/lib/date.ts
 * are used so we are never touching `new Date().toISOString()` directly (UTC drift).
 */

import type { Account, Budget, CurrencyRate, Transaction } from "../types";
import type { RecurringRule } from "../lib/life-os-contract";
import { recurringForMonth } from "./bento-derive";
import { entryAmountToVnd } from "./currency-derive";

// ─── types ───────────────────────────────────────────────────────────────────

export type EnvelopeResult = {
  /** The budget's monthly limit (unchanged). */
  limit: number;
  /** Cumulative carry from ALL months before `month` (positive = surplus, negative = deficit). */
  carryIn: number;
  /** Actual spending in `month` (mirrors bento-derive's spentForCategory). */
  spent: number;
  /** Effective budget headroom = limit + carryIn - spent (positive = remaining). */
  available: number;
};

/**
 * F8: pre-bucketed spend lookup shared across ALL budgets of one derive pass.
 * Replaces the per-month re-filter of the whole transactions array — build cost
 * is a single O(transactions) pass, lookups are O(1) per (category, month).
 */
export type SpendIndex = {
  spent(category: string, monthKey: string): number;
};

/**
 * Build a SpendIndex over the given state slices. Real expenses are bucketed
 * up-front by "monthKey|category"; recurring materialization stays per-month
 * (it depends on the month) but is cached, so each month is materialized once
 * per index instead of once per budget per walk iteration.
 */
export function buildSpendIndex(
  transactions: Transaction[],
  recurringRules: RecurringRule[],
  today: string,
  accounts: Account[] = [],
  rates: CurrencyRate[] = [],
): SpendIndex {
  const txByMonthCat = new Map<string, number>();
  for (const t of transactions) {
    if (t.type !== "expense" || t.toAccountId) continue;
    const key = `${t.date.slice(0, 7)}|${t.category.toLowerCase()}`;
    txByMonthCat.set(key, (txByMonthCat.get(key) ?? 0) + entryAmountToVnd(t.amount, t.accountId, accounts, rates));
  }

  const recurringByMonth = new Map<string, Map<string, number>>();
  function recurringBucket(monthKey: string): Map<string, number> {
    let bucket = recurringByMonth.get(monthKey);
    if (!bucket) {
      bucket = new Map();
      for (const e of recurringForMonth(recurringRules, monthKey, today)) {
        if (e.type !== "expense") continue;
        const cat = e.category.toLowerCase();
        bucket.set(cat, (bucket.get(cat) ?? 0) + entryAmountToVnd(e.amount, e.accountId, accounts, rates));
      }
      recurringByMonth.set(monthKey, bucket);
    }
    return bucket;
  }

  return {
    spent(category, monthKey) {
      const cat = category.toLowerCase();
      return (txByMonthCat.get(`${monthKey}|${cat}`) ?? 0) + (recurringBucket(monthKey).get(cat) ?? 0);
    },
  };
}

// ─── internal helpers ─────────────────────────────────────────────────────────

/** Advance a YYYY-MM key by 1 month. */
function nextMonthKey(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  if (m === 12) return `${y + 1}-01`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

/**
 * Compute spending for a category from flat arrays (mirrors bento-derive's logic exactly).
 * D46: amount quy đổi sang VND theo ví (accounts+rates) — khớp monthLedger.
 * accounts/rates mặc định [] → VND pass-through (giữ test cũ).
 */
function spentInMonth(
  category: string,
  transactions: Transaction[],
  recurringRules: RecurringRule[],
  monthKey: string,
  today: string,
  accounts: Account[] = [],
  rates: CurrencyRate[] = [],
): number {
  // Real transactions (exclude transfers — D5)
  const realExpense = transactions
    .filter(
      (t) =>
        t.type === "expense" &&
        !t.toAccountId &&
        t.date.slice(0, 7) === monthKey &&
        t.category.toLowerCase() === category.toLowerCase(),
    )
    .reduce((s, t) => s + entryAmountToVnd(t.amount, t.accountId, accounts, rates), 0);

  // Recurring rules materialized for this month
  const recurringExpense = recurringForMonth(recurringRules, monthKey, today)
    .filter(
      (e) =>
        e.type === "expense" &&
        e.category.toLowerCase() === category.toLowerCase(),
    )
    .reduce((s, e) => s + entryAmountToVnd(e.amount, e.accountId, accounts, rates), 0);

  return realExpense + recurringExpense;
}

/**
 * Find the earliest month with any activity (transaction or recurring rule) for a budget.
 * Falls back to `targetMonth` when no history exists (first-month case → carryIn = 0).
 *
 * We scan all real transactions whose category matches and pick the earliest one.
 * Recurring rules use their startDate. We cap to MAX_MONTHS_BACK before targetMonth.
 */
const MAX_MONTHS_BACK = 120; // 10 years — hard safety cap to prevent OOM

function earliestActivityMonth(
  category: string,
  transactions: Transaction[],
  recurringRules: RecurringRule[],
  targetMonth: string,
): string {
  // Earliest real transaction for this category
  const matchingTxDates = transactions
    .filter(
      (t) =>
        !t.toAccountId &&
        t.category.toLowerCase() === category.toLowerCase(),
    )
    .map((t) => t.date.slice(0, 7));

  // Earliest recurring rule for this category
  const matchingRuleMonths = recurringRules
    .filter((r) => r.category.toLowerCase() === category.toLowerCase())
    .map((r) => r.startDate.slice(0, 7));

  const allMonths = [...matchingTxDates, ...matchingRuleMonths];
  if (allMonths.length === 0) return targetMonth;

  const earliest = allMonths.sort()[0];

  // Safety: clamp to MAX_MONTHS_BACK before targetMonth
  const [ty, tm] = targetMonth.split("-").map(Number);
  const targetOrdinal = ty * 12 + tm;
  const [ey, em] = earliest.split("-").map(Number);
  const earliestOrdinal = ey * 12 + em;
  const clampedOrdinal = Math.max(earliestOrdinal, targetOrdinal - MAX_MONTHS_BACK);

  if (clampedOrdinal >= targetOrdinal) return targetMonth; // no prior months → carryIn = 0

  const cy = Math.floor((clampedOrdinal - 1) / 12);
  const cm = clampedOrdinal - cy * 12;
  return `${cy}-${String(cm).padStart(2, "0")}`;
}

// ─── public API ───────────────────────────────────────────────────────────────

/**
 * Compute the true envelope for a budget in `month`, walking the full history
 * since first activity.
 *
 * - Non-rollover budgets: always carryIn = 0 (fast-path, O(1)).
 * - Rollover budgets: single forward pass, O(months × transactions) worst case,
 *   but transactions are filtered by category up front so real cost is low.
 *
 * @param budget          The budget definition (monthlyLimit, rollover, category).
 * @param transactions    ALL transactions in state (we slice by month internally).
 * @param recurringRules  ALL recurring rules.
 * @param month           Target month as YYYY-MM.
 * @param today           ISO date string for recurring materialization (pass for determinism in tests).
 */
export function budgetEnvelope(
  budget: Budget,
  transactions: Transaction[],
  recurringRules: RecurringRule[],
  month: string,
  today: string,
  accounts: Account[] = [],
  rates: CurrencyRate[] = [],
  // F8: pass a shared SpendIndex when deriving MANY budgets (budgetStatuses) —
  // omitted → per-call filtering as before (existing callers/tests unchanged).
  index?: SpendIndex,
): EnvelopeResult {
  const limit = budget.monthlyLimit;
  const spentIn = index
    ? (mk: string) => index.spent(budget.category, mk)
    : (mk: string) => spentInMonth(budget.category, transactions, recurringRules, mk, today, accounts, rates);

  // Non-rollover: trivial path
  if (!budget.rollover) {
    const spent = spentIn(month);
    return { limit, carryIn: 0, spent, available: limit - spent };
  }

  // Rollover: find earliest month and walk forward accumulating carry
  const startMonth = earliestActivityMonth(budget.category, transactions, recurringRules, month);

  let carryIn = 0;
  let cursor = startMonth;

  // Guard: if startMonth >= month there is no prior history
  if (cursor >= month) {
    const spent = spentIn(month);
    return { limit, carryIn: 0, spent, available: limit - spent };
  }

  // Single forward pass over months prior to `month`
  let iterations = 0;
  while (cursor < month && iterations < MAX_MONTHS_BACK) {
    const monthSpent = spentIn(cursor);
    // carry += (limit − spent): positive surplus adds headroom, over-spend subtracts
    carryIn += limit - monthSpent;
    cursor = nextMonthKey(cursor);
    iterations++;
  }

  const spent = spentIn(month);
  return { limit, carryIn, spent, available: limit + carryIn - spent };
}
